import React, { useState, FC } from 'react';
import { Popover, Input } from 'antd';
import { Button, Loading } from 'Components/nui';
import { FormattedMessage } from 'react-intl';
import { usePresets, useCreatePreset } from '~/hooks/useOrderPresets';

interface PresetButtonProps {
  product: any;
  orderType: 'bid' | 'offer';
  formWrapper: any;
}

interface PopoverContentProps extends PresetButtonProps {
  setVisible: (visible: boolean) => void;
}

const PopoverContent: FC<PopoverContentProps> = ({
  product,
  orderType,
  formWrapper,
  setVisible,
}) => {
  const { data: presets = [], isLoading } = usePresets();
  const createPreset = useCreatePreset();

  const [name, setName] = useState('');

  const save = async () => {
    if (!name) return;

    const data = {
      name,
      product: product.id,
      sell: orderType === 'offer',
      ...formWrapper.serialize(),
    };

    try {
      await createPreset.mutateAsync(data);
      setVisible(false);
      setName('');
    } catch (err: any) {
      const errors = err?.response?.data?.errors?.[0]?.description;
      if (errors) {
        formWrapper.setErrors(errors);
        window.scrollTo(0, 0);
      }
      // Error toast already handled in hook
    }
  };

  if (isLoading) return <Loading size="small" />;

  if (presets.length >= 20)
    return (
      <div className="preset-name">
        You have reached the limit of 20 presets.
        <br />
        To be able to save new presets, please remove unnecessary
        <br />
        from the list of the existing presets.
      </div>
    );

  return (
    <div className="preset-name">
      <Input
        placeholder="Name"
        style={{ width: 250 }}
        value={name}
        onChange={(e) => setName(e.target.value)}
        onPressEnter={save}
      />{' '}
      <Button
        htmlType="button"
        onClick={save}
        loading={createPreset.isPending}
        disabled={!name}
      >
        <FormattedMessage
          id="marketplace-filter-presets-field-save-button"
          description="Button for 'save preset' field in Marketplace filter"
          defaultMessage="Save"
        />
      </Button>
    </div>
  );
};

export const PresetButton: FC<PresetButtonProps> = (props) => {
  const [visible, setVisible] = useState(false);

  return (
    <Popover
      content={<PopoverContent {...props} setVisible={setVisible} />}
      title={
        <FormattedMessage
          id="orderform-button-preset-modal-title"
          description="Title 'Preset name' for preset modal in new order form"
          defaultMessage="Preset name"
        />
      }
      trigger="click"
      visible={visible}
      onVisibleChange={(v) => setVisible(v)}
    >
      <Button htmlType="button" type="simple" className="ml-20 pl-0 pr-0">
        <FormattedMessage
          id="orderform-button-save-preset"
          description="button for 'Save as preset' in new order form"
          defaultMessage="Save as preset"
        />
      </Button>
    </Popover>
  );
};
